import _ from 'lodash'
import * as THREE from 'three'

import MathsUtils from '@/utils/mathsUtils'
import BaseObject from '../objects/baseObject'
import RectObject from '../objects/rectObject'

import GlassDesignerTypes from '@glass-designer/types'


/*
 * GlassShapeGroup
 * Contains a list of vertices in the shape that are grouped together
 * Most commonly used for grouping all vertices that belong to an edge/corner cutout
 */
export default class GlassShapeGroup extends BaseObject {
  constructor(editor, options = {}) {
    super(editor)
    this.objectClassName = 'GlassShapeGroup'

    this.outlineGeometry = null
    this.outlineObj = null
    this.rectangleObjList = []

    this.listenMouseEvents = true
    this.dashedLineScale = 1.25
    this.displayPadding = 5

    this.sceneObject = new THREE.Object3D()
    this.editor.scene.add(this.sceneObject)

    // Options
    this.isCutout = !!options.isCutout
    this.cutoutType = options.cutoutType || GlassDesignerTypes.CutoutTypes.edge
    this.cutoutMeasurementType = options.cutoutMeasurementType || GlassDesignerTypes.CutoutMeasurementTypes.center

    // Stock Overrides
    // If stock override is set for cutout groups, the cutout-detection in OrderHelper will be ignored and these stock items will be added
    // Override list is a list of objects with 'id' set to represent the stock item 
    this.isCutoutStockOverride = !!options.isCutoutStockOverride
    this.cutoutStockOverrideList = this._parseStockOverrideList(options.cutoutStockOverrideList || [])

    // List of all the segment IDs that have their start point in this group
    this.segmentIdList = []

    // Cached point list of the segment start/end positions
    // Note: Not persisted, regenerated when the segment list is updated
    this.cachedPointList = []   // Points along the path used for bounds/sizing calculations
    this.storedSegmentList = []   // Start/end positions used for group merging

    // Flag based on segments, cached & not persisted
    this.cutoutPolished = false
    
    // Precalculated bounds of the group
    this.bounds = new THREE.Box2()
    this.highlightBounds = new THREE.Box2()

    for(var i=0; i<4; i++) {
      let cornerRect = new RectObject(this.editor, {
        width: 5,
        height: 5,
        filled: true,
        colour: this.editor.getTheme().groupColour
      })
      this.addChildObject(cornerRect)
      this.rectangleObjList.push(cornerRect)
    }

    if(options.segmentList)
      this.updateGroupSegments(options.segmentList)
  }

  // Dispose the object
  dispose() {
    super.dispose()

    // Remove stuff
    if(this.outlineGeometry) {
      this.outlineGeometry.dispose()
      this.outlineGeometry = null
    }
    this.outlineObj = null
  }


  /**
   * Check if a segment is linked to this group
   */
  isSegmentInGroup(segmentId) {
    return this.segmentIdList.find(listSegId => listSegId == segmentId) != null
  }


  /**
   * Get polished flag for the group
   * If at least 1 segment in the group is polished, the group is considered polished for cutout purposes
   */
  isGroupCutoutPolished() {
    return this.cutoutPolished
  }


  /**
   * Set the cutout polished flag for the group
   * Applies the polished flag for all segments inside the group
   */
  setGroupCutoutPolished(polished) {
    if(!this.parentObject)
      return

    this.cutoutPolished = polished

    this.segmentIdList.forEach(segmentId => {
      let segment = this.parentObject.getSegmentById(segmentId)
      if(segment)
        segment.isCutoutPolished = polished
    })
  }


  /**
   * Update the segment info & recalculate points/bounds
   * Done if the shape was moved/edited
   */
  updateGroupSegments(segmentList) {
    // Clear info & re-add segments
    this.segmentIdList = segmentList.map(segment => segment.id)

    this._segmentListUpdated(segmentList)
    this.updateSceneObject()
  }


  /**
   * Segment list updated, update the cached point/segment info
   */
  _segmentListUpdated(segmentList = null) {
    this.cachedPointList = []
    this.storedSegmentList = []

    this.bounds.makeEmpty()
    this.highlightBounds.makeEmpty()

    // If at least one segment is polished, the group is polished & we'll flag all segments as polished
    this.cutoutPolished = false
    if(this.parentObject) {
      var polishedCount = 0
      this.segmentIdList.forEach(segmentId => {
        let segment = this.parentObject.getSegmentById(segmentId)
        if(segment && segment.isCutoutPolished)
          polishedCount++
      })

      if(polishedCount > 0)
        this.cutoutPolished = true
    }

    this.segmentIdList.forEach(segmentId => {
      let segment = null
      // Use passed in segment list, otherwise attempt to get segment from shape parent
      if(segmentList) {
        segment = segmentList.find(seg => seg.id == segmentId)
      } else {
        if(this.parentObject)
          segment = this.parentObject.getSegmentById(segmentId)
      }

      if(segment) {
        this.bounds.expandByPoint(segment.startPosition)
        this.bounds.expandByPoint(segment.endPosition)

        // Store segment info for merge support
        this.storedSegmentList.push(segment.clone())

        this.cachedPointList.push(segment.startPosition.clone())
        this.cachedPointList.push(segment.endPosition.clone())

        segment.isCutoutPolished = this.cutoutPolished

        // Additional points for arcs
        if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
          let arcPoints = [segment.getPoint(0.25), segment.getPoint(0.5), segment.getPoint(0.75)]

          arcPoints.forEach(pnt => {
            this.bounds.expandByPoint(pnt)
            this.cachedPointList.push(pnt)
          })
        }
      }
    })
    this.highlightBounds = this.bounds.clone()
    this.highlightBounds.expandByScalar(this.displayPadding)
  }


  /**
   * Mouse Move Event
   */
  onMouseMoveEvent(ev, mouseData, intersectionData = {}) {
    if(this.editor.isSelectEnabled())
      this.editor.setMouseCursorHand()
  }


  /**
   * Update the view level on the object
   * Adjusts the scene object matrix & visibility for editing / viewing modes
   */
  viewLevelUpdated(viewLevel) {
    // Only show the group boxes in normal mode
    let visible = viewLevel == GlassDesignerTypes.EditorViewLevelTypes.normal

    if(this.outlineObj)
      this.outlineObj.visible = visible
    this.rectangleObjList.forEach(rectObj => {
      rectObj.setVisible(visible)
    })
  }


  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    var renderScale = 1.0 / this.editor.camera.zoom

    let colour = this.editor.getTheme().groupColour
    if(this.isGroupCutoutPolished())
      colour = this.editor.getTheme().groupPolishedColour
    if(this.selected)
      colour = this.editor.getTheme().selectionColour

    // Scale the dashes relative to the camera
    if(this.outlineObj) {
      this.outlineObj.material.scale = this.editor.camera.zoom * this.dashedLineScale
      this.outlineObj.material.color.set(colour)
    }
    this.rectangleObjList.forEach(rectObj => {
      rectObj.scale.x = renderScale
      rectObj.scale.y = renderScale
      rectObj.colour = colour
    })
  }


  // Build the group object graphics
  updateSceneObject() {
    if(this.bounds.isEmpty())
      return

    this.clearSceneObject()

    if(this.outlineGeometry) {
      this.outlineGeometry.dispose()
      this.outlineGeometry = null
    }
    this.outlineObj = null

    // Circle outline around the bounds of the grouped vertices
    let groupCenter = this.highlightBounds.getCenter(new THREE.Vector2())
    let groupSize = this.highlightBounds.getSize(new THREE.Vector2())

    let groupHalfW = groupSize.x / 2
    let groupHalfH = groupSize.y / 2

    this.rectangleObjList[0].setPosition(new THREE.Vector2(groupCenter.x - groupHalfW, groupCenter.y - groupHalfH))
    this.rectangleObjList[1].setPosition(new THREE.Vector2(groupCenter.x + groupHalfW, groupCenter.y - groupHalfH))
    this.rectangleObjList[2].setPosition(new THREE.Vector2(groupCenter.x + groupHalfW, groupCenter.y + groupHalfH))
    this.rectangleObjList[3].setPosition(new THREE.Vector2(groupCenter.x - groupHalfW, groupCenter.y + groupHalfH))

    var groupShape = new THREE.Shape()
    groupShape.moveTo(groupCenter.x - groupHalfW, groupCenter.y - groupHalfH)
    groupShape.lineTo(groupCenter.x + groupHalfW, groupCenter.y - groupHalfH)
    groupShape.lineTo(groupCenter.x + groupHalfW, groupCenter.y + groupHalfH)
    groupShape.lineTo(groupCenter.x - groupHalfW, groupCenter.y + groupHalfH)
    groupShape.lineTo(groupCenter.x - groupHalfW, groupCenter.y - groupHalfH)
    var vertices = []

    let positionZ = 200
    var shapePoints = groupShape.getPoints(15)
    for(var i = 0; i < shapePoints.length - 1; i++)
      vertices.push(shapePoints[i].x , shapePoints[i].y, positionZ, shapePoints[(i + 1) % shapePoints.length].x , shapePoints[(i + 1) % shapePoints.length].y, positionZ)

    this.outlineGeometry = new THREE.BufferGeometry(groupShape)
    this.outlineGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute(vertices, 3))

    var material = new THREE.LineDashedMaterial({
      color: this.editor.getTheme().groupColour,
      transparent: false,
      dashSize: 6,
      gapSize: 4,
      scale: this.dashedLineScale
    })

    this.outlineObj = new THREE.LineSegments(this.outlineGeometry, material)
    this.outlineObj.computeLineDistances()
    this.sceneObject.add(this.outlineObj)
  }


  /**
   * Calculate the group width in a direction given a directional vector
   */
  calculateGroupWidthForDirection(direction) {
    return MathsUtils.calculatePointListDistanceForDirection(this.cachedPointList, direction)    
  }


  /**
   * Calculate the group width distances (min/max) in a direction given a directional vector
   * Returns an object with min & max properties for distances
   * Calculated relative to the first point in the group or a given point
   */
  calculateGroupRangeInfoForDirection(direction, relativePoint = null) {
    return MathsUtils.calculatePointListDistanceRangeForDirection(this.cachedPointList, direction, relativePoint)    
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    // Ignore if not visible
    if(!this.isVisible())
      return null

    var closestIntersection = null
    let radiusTest = Math.max(worldRadius, 0.1)

    // Create a plane from the glass shape
    // Assumes the 2D shape is sitting on the plane at origin (Z = 0)
    var shapePlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(shapePlane, rayInt))
      return null

    var ray2DPoint = new THREE.Vector2(rayInt.x, rayInt.y)

    let dist = this.highlightBounds.distanceToPoint(ray2DPoint)
    
    if(dist <= radiusTest) {
      closestIntersection = {
        intersection: ray2DPoint,
        position: ray2DPoint,
        objectId: this.id,
        distance: dist
      }
    }

    return closestIntersection
  }


  /**
   * Update the stock override list
   */
  updateStockOverrideList(stockOverrides) {
    this.cutoutStockOverrideList = this._parseStockOverrideList(stockOverrides)
  }


  /**
   * Parse the stock override list into an array
   * Processes only required fields & filters out invalid stock overrides
   */
  _parseStockOverrideList(list) {
    if(!list)
      return []

    return _.compact(list.map(stockOverride => {
      var outputStock = {
        id: parseInt(stockOverride.id || 0),
        code: stockOverride.code || '',
        name: stockOverride.name || ''
      }

      if(!outputStock.id || isNaN(outputStock.id))
        return null
      return outputStock
    }))
  }


  /**
   * Clone the glass shape group object
   */
  clone() {
    let newGroup = new GlassShapeGroup(this.editor)

    newGroup.segmentIdList = _.cloneDeep(this.segmentIdList)
    newGroup.bounds = this.bounds.clone()
    newGroup.highlightBounds = this.highlightBounds.clone()
    newGroup.isCutout = this.isCutout
    newGroup.cutoutType = this.cutoutType
    newGroup.cutoutMeasurementType = this.cutoutMeasurementType
    newGroup.isCutoutStockOverride = this.isCutoutStockOverride
    newGroup.cutoutStockOverrideList = this._parseStockOverrideList(this.cutoutStockOverrideList || [])

    newGroup._segmentListUpdated()
    newGroup.updateSceneObject()

    return newGroup
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {    
    var outputData = super.serialize()

    outputData.segmentIdList = _.cloneDeep(this.segmentIdList)
    outputData.bounds = MathsUtils.serializeBox2(this.bounds)
    outputData.highlightBounds = MathsUtils.serializeBox2(this.highlightBounds)
    outputData.isCutout = this.isCutout
    outputData.cutoutType = this.cutoutType
    outputData.cutoutMeasurementType = this.cutoutMeasurementType
    outputData.isCutoutStockOverride = this.isCutoutStockOverride
    outputData.cutoutStockOverrideList = _.cloneDeep(this.cutoutStockOverrideList)

    return outputData
  }
  deserialize(object) {
    super.deserialize(object)

    this.segmentIdList = _.cloneDeep(object.segmentIdList)
    this.bounds = MathsUtils.deserializeBox2(object.bounds)
    this.highlightBounds = MathsUtils.deserializeBox2(object.highlightBounds)
    this.isCutout = !!object.isCutout
    this.cutoutType = object.cutoutType || GlassDesignerTypes.CutoutTypes.edge
    this.cutoutMeasurementType = object.cutoutMeasurementType || GlassDesignerTypes.CutoutMeasurementTypes.center
    this.isCutoutStockOverride = !!object.isCutoutStockOverride
    this.cutoutStockOverrideList = this._parseStockOverrideList(object.cutoutStockOverrideList || [])

    this._segmentListUpdated()
    this.updateSceneObject()
  }

}